import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronDown, Plus, X, Upload, Camera, Link as LinkIcon, Phone, FileText, MapPin, StickyNote } from 'lucide-react';
import api from '../lib/api';
import { createEvent, fetchEvent, updateEvent, EVENT_TYPES, RECURRENCE_OPTIONS } from '../lib/events';
import { PrimaryButton } from '../components/Button';
import { TextInput } from '../components/Input';

const DAY_LETTERS = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];

export default function NewEvent() {
    const navigate = useNavigate();
    const { eventId } = useParams();
    const isEdit = Boolean(eventId);

    const [type, setType] = useState('evento');
    const [title, setTitle] = useState('');
    const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
    const [time, setTime] = useState('09:00');
    const [allDay, setAllDay] = useState(false);
    const [imageUrl, setImageUrl] = useState('');
    const [description, setDescription] = useState('');
    const [address, setAddress] = useState('');
    const [links, setLinks] = useState([]);
    const [linkDraft, setLinkDraft] = useState({ label: '', url: '' });
    const [contacts, setContacts] = useState([]);
    const [contactDraft, setContactDraft] = useState({ name: '', phone: '' });
    const [amount, setAmount] = useState('');
    const [isRecurring, setIsRecurring] = useState(false);
    const [recurrence, setRecurrence] = useState({ frequency: 'weekly', interval: 1, days_of_week: [], until: '' });
    const [members, setMembers] = useState([]);
    const [notifyMembers, setNotifyMembers] = useState([]);
    const [openSections, setOpenSections] = useState({ nota: false, direccion: false, links: false, contactos: false, documentos: false });
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get('/family/members').then((r) => setMembers(r.data.members || [])).catch(() => {});
        if (isEdit) {
            fetchEvent(eventId).then((e) => {
                setType(e.type); setTitle(e.title); setDate(e.date); setTime(e.time || '09:00'); setAllDay(e.all_day);
                setImageUrl(e.image_url || ''); setDescription(e.description || ''); setAddress(e.address || '');
                setLinks(e.links || []); setContacts(e.contacts || []);
                setAmount(e.amount ? String(e.amount) : '');
                if (e.recurrence) { setIsRecurring(true); setRecurrence({ ...recurrence, ...e.recurrence }); }
                setNotifyMembers(e.notify_members || []);
                setOpenSections({
                    nota: !!e.description, direccion: !!e.address,
                    links: (e.links || []).length > 0, contactos: (e.contacts || []).length > 0,
                    documentos: (e.documents || []).length > 0,
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId]);

    const submit = async () => {
        setError('');
        if (!title.trim()) { setError('Ponle un título al evento'); return; }
        setSaving(true);
        try {
            const payload = {
                type, title: title.trim(),
                description: description.trim() || null,
                date, time: allDay ? null : time, all_day: allDay,
                image_url: imageUrl.trim() || null,
                address: address.trim() || null,
                links: links.filter((l) => l.url),
                contacts: contacts.filter((c) => c.name),
                notify_members: notifyMembers,
                recurrence: isRecurring ? recurrence : null,
                amount: amount ? parseFloat(amount) : null,
            };
            const result = isEdit
                ? await updateEvent(eventId, payload)
                : await createEvent(payload);
            navigate(`/agenda/${result.id}`, { replace: true });
        } catch (e) {
            setError(e?.response?.data?.detail || 'Error al guardar');
        } finally { setSaving(false); }
    };

    const toggleDayOfWeek = (i) => {
        const days = recurrence.days_of_week.includes(i)
            ? recurrence.days_of_week.filter((x) => x !== i)
            : [...recurrence.days_of_week, i];
        setRecurrence({ ...recurrence, days_of_week: days });
    };
    const toggleSection = (k) => setOpenSections({ ...openSections, [k]: !openSections[k] });
    const toggleMember = (id) => setNotifyMembers((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

    return (
        <div className="min-h-screen bg-surface app-shell pb-32">
            <div className="px-6 pt-4">
                <div className="flex items-center justify-between">
                    <button onClick={() => navigate(-1)} className="w-10 h-10 -ml-2 hover:bg-gray-100 rounded-full flex items-center justify-center" data-testid="new-event-back"><ArrowLeft className="w-5 h-5 text-brand-navy" /></button>
                    <h1 className="font-bold text-brand-navy">{isEdit ? 'Editar evento' : 'Nuevo evento'}</h1>
                    <button onClick={submit} disabled={saving} className="px-4 h-9 rounded-full bg-brand text-white text-sm font-bold disabled:opacity-50" data-testid="new-event-save">{saving ? 'Guardando…' : 'Guardar'}</button>
                </div>

                {error && <div className="mt-3 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-xl" data-testid="new-event-error">{error}</div>}

                <Field label="Imagen principal" optional rightLabel="(opcional)">
                    <div className="bg-white rounded-2xl border border-gray-200 p-4">
                        <TextInput value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="URL de imagen (opcional)" testId="new-event-image" />
                        <div className="mt-3 grid grid-cols-2 gap-2">
                            <button disabled className="h-10 rounded-xl bg-gray-50 border border-dashed border-gray-300 text-gray-400 text-xs flex items-center justify-center gap-1 cursor-not-allowed"><Upload className="w-3 h-3" /> Subir</button>
                            <button disabled className="h-10 rounded-xl bg-gray-50 border border-dashed border-gray-300 text-gray-400 text-xs flex items-center justify-center gap-1 cursor-not-allowed"><Camera className="w-3 h-3" /> Foto</button>
                        </div>
                    </div>
                </Field>

                <Field label="Título del evento">
                    <TextInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej: Cumpleaños de Mateo" testId="new-event-title" />
                </Field>

                <div className="mt-5 grid grid-cols-2 gap-3">
                    <Field label="Fecha">
                        <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full h-14 bg-white border border-gray-200 rounded-2xl px-4 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20" data-testid="new-event-date" />
                    </Field>
                    <Field label="Hora">
                        <input type="time" value={time} onChange={(e) => setTime(e.target.value)} disabled={allDay} className="w-full h-14 bg-white border border-gray-200 rounded-2xl px-4 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:opacity-50" data-testid="new-event-time" />
                    </Field>
                </div>

                <Field label="Tipo de evento">
                    <Select value={type} onChange={setType} options={EVENT_TYPES.map((t) => ({ value: t.value, label: `${t.icon} ${t.label}` }))} testId="new-event-type" />
                </Field>

                {type === 'pago' && (
                    <Field label="Monto">
                        <TextInput type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" testId="new-event-amount" />
                    </Field>
                )}

                <div className="mt-6 bg-white rounded-2xl p-4 shadow-card">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm font-bold text-brand-navy">Evento recurrente</div>
                            <div className="text-xs text-gray-500">Se repetirá automáticamente</div>
                        </div>
                        <button onClick={() => setIsRecurring(!isRecurring)} className={`relative w-11 h-6 rounded-full transition ${isRecurring ? 'bg-brand' : 'bg-gray-300'}`} data-testid="new-event-recurring-toggle">
                            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition ${isRecurring ? 'left-5' : 'left-0.5'}`} />
                        </button>
                    </div>
                    {isRecurring && (
                        <div className="mt-4 space-y-3">
                            <Select value={recurrence.frequency} onChange={(v) => setRecurrence({ ...recurrence, frequency: v })} options={RECURRENCE_OPTIONS} testId="new-event-frequency" />
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">Cada</span>
                                <input type="number" min="1" value={recurrence.interval} onChange={(e) => setRecurrence({ ...recurrence, interval: Math.max(1, parseInt(e.target.value || 1)) })} className="w-16 h-10 bg-gray-50 border border-gray-200 rounded-xl text-center text-sm" />
                                <span className="text-xs text-gray-500">{recurrence.frequency === 'daily' ? 'día(s)' : recurrence.frequency === 'weekly' ? 'semana(s)' : recurrence.frequency === 'monthly' ? 'mes(es)' : 'año(s)'}</span>
                            </div>
                            {recurrence.frequency === 'weekly' && (
                                <div>
                                    <div className="text-xs font-semibold text-gray-500 mb-1">Días de la semana</div>
                                    <div className="flex gap-1.5">
                                        {DAY_LETTERS.map((d, i) => {
                                            const active = recurrence.days_of_week.includes(i);
                                            return (
                                                <button key={i} onClick={() => toggleDayOfWeek(i)} className={`w-9 h-9 rounded-full text-xs font-bold transition ${active ? 'bg-brand text-white' : 'bg-gray-100 text-gray-500'}`} data-testid={`new-event-day-${i}`}>{d}</button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                            <div>
                                <div className="text-xs font-semibold text-gray-500 mb-1">Termina el (opcional)</div>
                                <input type="date" value={recurrence.until} onChange={(e) => setRecurrence({ ...recurrence, until: e.target.value })} className="w-full h-10 bg-gray-50 border border-gray-200 rounded-xl px-3 text-sm" data-testid="new-event-until" />
                            </div>
                        </div>
                    )}
                </div>

                <div className="mt-6">
                    <h3 className="text-[11px] uppercase tracking-wider font-bold text-gray-500 mb-3">Contenido complementario</h3>
                    <div className="flex flex-wrap gap-2">
                        <SectionPill icon={StickyNote} label="Nota" active={openSections.nota} onClick={() => toggleSection('nota')} />
                        <SectionPill icon={MapPin} label="Dirección" active={openSections.direccion} onClick={() => toggleSection('direccion')} />
                        <SectionPill icon={LinkIcon} label="Links" active={openSections.links} onClick={() => toggleSection('links')} />
                        <SectionPill icon={Phone} label="Contactos" active={openSections.contactos} onClick={() => toggleSection('contactos')} />
                        <SectionPill icon={FileText} label="Documentos" active={openSections.documentos} onClick={() => toggleSection('documentos')} comingSoon />
                    </div>

                    {openSections.nota && (
                        <div className="mt-3">
                            <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Añade información extra del evento…" rows={4} className="w-full bg-white border border-gray-200 rounded-2xl p-3 text-sm focus:border-brand focus:ring-2 focus:ring-brand/20" data-testid="new-event-note" />
                        </div>
                    )}
                    {openSections.direccion && (
                        <div className="mt-3">
                            <TextInput value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Ej: Av. Libertador 1234" testId="new-event-address" />
                        </div>
                    )}
                    {openSections.links && (
                        <div className="mt-3 space-y-2">
                            {links.map((l, i) => (
                                <div key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-3 py-2" data-testid={`new-event-link-${i}`}>
                                    <span className="flex-1 text-sm truncate">{l.label || l.url}</span>
                                    <button onClick={() => setLinks(links.filter((_, idx) => idx !== i))}><X className="w-4 h-4 text-gray-400" /></button>
                                </div>
                            ))}
                            <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-3 space-y-2">
                                <TextInput value={linkDraft.label} onChange={(e) => setLinkDraft({ ...linkDraft, label: e.target.value })} placeholder="Etiqueta (ej: Portal del colegio)" />
                                <TextInput value={linkDraft.url} onChange={(e) => setLinkDraft({ ...linkDraft, url: e.target.value })} placeholder="https://…" />
                                <button onClick={() => { if (linkDraft.url) { setLinks([...links, linkDraft]); setLinkDraft({ label: '', url: '' }); } }} className="w-full h-9 rounded-full bg-brand/10 text-brand text-xs font-bold flex items-center justify-center gap-1"><Plus className="w-3 h-3" /> Agregar enlace</button>
                            </div>
                        </div>
                    )}
                    {openSections.contactos && (
                        <div className="mt-3 space-y-2">
                            {contacts.map((c, i) => (
                                <div key={i} className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-3 py-2" data-testid={`new-event-contact-${i}`}>
                                    <span className="w-8 h-8 rounded-full bg-brand/10 text-brand text-xs font-bold flex items-center justify-center">{(c.name || '?').charAt(0)}</span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm font-semibold text-brand-navy truncate">{c.name}</div>
                                        <div className="text-[11px] text-gray-500">{c.phone}</div>
                                    </div>
                                    <button onClick={() => setContacts(contacts.filter((_, idx) => idx !== i))}><X className="w-4 h-4 text-gray-400" /></button>
                                </div>
                            ))}
                            <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-3 space-y-2">
                                <TextInput value={contactDraft.name} onChange={(e) => setContactDraft({ ...contactDraft, name: e.target.value })} placeholder="Nombre" />
                                <TextInput value={contactDraft.phone} onChange={(e) => setContactDraft({ ...contactDraft, phone: e.target.value })} placeholder="Teléfono" />
                                <button onClick={() => { if (contactDraft.name) { setContacts([...contacts, contactDraft]); setContactDraft({ name: '', phone: '' }); } }} className="w-full h-9 rounded-full bg-brand/10 text-brand text-xs font-bold flex items-center justify-center gap-1"><Plus className="w-3 h-3" /> Agregar contacto</button>
                            </div>
                        </div>
                    )}
                    {openSections.documentos && (
                        <div className="mt-3 bg-amber-50 border border-amber-200 rounded-2xl p-3 text-xs text-amber-700">
                            📦 Subida de documentos próximamente. Por ahora, agrégalos como enlaces.
                        </div>
                    )}
                </div>

                <div className="mt-6 bg-white rounded-2xl p-4 shadow-card">
                    <h3 className="text-sm font-bold text-brand-navy mb-3">Notificar a</h3>
                    <div className="flex gap-3 overflow-x-auto pb-2">
                        {members.map((m) => {
                            const active = notifyMembers.includes(m.id);
                            return (
                                <button key={m.id} onClick={() => toggleMember(m.id)} className="flex flex-col items-center gap-1 min-w-[60px]" data-testid={`new-event-notify-${m.id}`}>
                                    <span className={`w-12 h-12 rounded-full flex items-center justify-center text-sm font-bold border-2 transition ${active ? 'border-brand bg-brand/10 text-brand' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>{(m.name || '?').charAt(0).toUpperCase()}</span>
                                    <span className="text-[10px] text-gray-600 truncate w-full text-center">{(m.name || '').split(' ')[0]}</span>
                                </button>
                            );
                        })}
                        {members.length === 0 && <p className="text-xs text-gray-400 italic">Sin miembros</p>}
                    </div>
                </div>
            </div>

            <div className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[480px] bg-white border-t border-gray-100 px-6 py-3 z-30">
                <PrimaryButton onClick={submit} loading={saving} testId="new-event-submit">{isEdit ? 'Guardar cambios' : 'Crear evento'}</PrimaryButton>
            </div>
        </div>
    );
}

function Field({ label, rightLabel, children }) {
    return (
        <div className="mt-5">
            <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-semibold text-gray-800">{label}</label>
                {rightLabel && <span className="text-xs text-gray-400">{rightLabel}</span>}
            </div>
            {children}
        </div>
    );
}
function Select({ value, onChange, options, testId }) {
    return (
        <div className="relative">
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId} className="w-full h-14 bg-white border border-gray-200 rounded-2xl px-4 pr-10 text-sm appearance-none focus:border-brand focus:ring-2 focus:ring-brand/20">
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <ChevronDown className="w-4 h-4 text-gray-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
    );
}
function SectionPill({ icon: Icon, label, active, onClick, comingSoon }) {
    return (
        <button onClick={onClick} className={`flex items-center gap-1.5 h-9 px-3 rounded-full text-xs font-bold transition ${active ? 'bg-brand text-white' : 'bg-white text-gray-600 border border-gray-200'}`}>
            <Icon className="w-3.5 h-3.5" /> {label} {comingSoon && <span className="text-[8px] opacity-70">próx.</span>}
        </button>
    );
}
